/**
 * Request Manager - Circuit Breaker Pattern
 * Prevents request storms when backend is down
 */

import { createLogger } from '../utils/logger';

// Create a logger specifically for RequestManager
const logger = createLogger('RequestManager');

export type BackendHealthStatus = 'healthy' | 'degraded' | 'unhealthy';

interface RequestManagerConfig {
    failureThreshold: number;  // Number of failures before opening circuit
    recoveryTimeout: number;   // Time in ms before attempting recovery
    halfOpenRequests: number;  // Number of test requests in half-open state
}

const DEFAULT_CONFIG: RequestManagerConfig = {
    failureThreshold: 10,       // Very relaxed: 10 failures before warning (was 3)
    recoveryTimeout: 10000,     // Quick recovery: 10 seconds (was 30)
    halfOpenRequests: 3,        // More test requests allowed (was 1)
};

class RequestManager {
    private static instance: RequestManager;
    private config: RequestManagerConfig;

    // Circuit breaker state
    private failureCount = 0;
    private lastFailureTime = 0;
    private circuitState: 'closed' | 'open' | 'half-open' = 'closed';
    private halfOpenRequestCount = 0;

    // Event listeners for status changes
    private listeners: Set<(status: BackendHealthStatus) => void> = new Set();

    private constructor(config: Partial<RequestManagerConfig> = {}) {
        this.config = { ...DEFAULT_CONFIG, ...config };
    }

    static getInstance(config?: Partial<RequestManagerConfig>): RequestManager {
        if (!RequestManager.instance) {
            RequestManager.instance = new RequestManager(config);
        }
        return RequestManager.instance;
    }

    /**
     * Check if requests should be allowed
     */
    shouldAllowRequest(): boolean {
        this.updateCircuitState();

        switch (this.circuitState) {
            case 'closed':
                return true;

            case 'open':
                return false;

            case 'half-open':
                if (this.halfOpenRequestCount < this.config.halfOpenRequests) {
                    this.halfOpenRequestCount++;
                    return true;
                }
                return false;

            default:
                return true;
        }
    }

    /**
     * Record a successful request
     */
    recordSuccess(): void {
        this.failureCount = 0;
        this.halfOpenRequestCount = 0;

        if (this.circuitState !== 'closed') {
            logger.log('Circuit closed - backend recovered');
            this.circuitState = 'closed';
            this.notifyListeners('healthy');
        }
    }

    /**
     * Record a failed request
     */
    recordFailure(): void {
        this.failureCount++;
        this.lastFailureTime = Date.now();

        logger.log(`Failure recorded (${this.failureCount}/${this.config.failureThreshold})`);

        if (this.circuitState === 'half-open') {
            // Failure during half-open means backend still unhealthy
            logger.log('Half-open test failed - reopening circuit');
            this.circuitState = 'open';
            this.halfOpenRequestCount = 0;
            this.notifyListeners('unhealthy');
        } else if (this.failureCount >= this.config.failureThreshold) {
            if (this.circuitState !== 'open') {
                logger.log('Circuit opened - backend appears unhealthy');
                this.circuitState = 'open';
                this.notifyListeners('unhealthy');
            }
        } else {
            this.notifyListeners('degraded');
        }
    }

    /**
     * Update circuit state based on time
     */
    private updateCircuitState(): void {
        if (this.circuitState === 'open') {
            const timeSinceFailure = Date.now() - this.lastFailureTime;

            if (timeSinceFailure >= this.config.recoveryTimeout) {
                logger.log('Entering half-open state - testing backend');
                this.circuitState = 'half-open';
                this.halfOpenRequestCount = 0;
                this.notifyListeners('degraded');
            }
        }
    }

    /**
     * Get current health status
     */
    getHealthStatus(): BackendHealthStatus {
        this.updateCircuitState();

        switch (this.circuitState) {
            case 'closed':
                return 'healthy';
            case 'half-open':
                return 'degraded';
            case 'open':
                return 'unhealthy';
            default:
                return 'healthy';
        }
    }

    /**
     * Check if circuit is open (blocking requests)
     */
    isCircuitOpen(): boolean {
        this.updateCircuitState();
        return this.circuitState === 'open';
    }

    /**
     * Subscribe to health status changes
     */
    subscribe(listener: (status: BackendHealthStatus) => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    /**
     * Notify all listeners of status change
     */
    private notifyListeners(status: BackendHealthStatus): void {
        this.listeners.forEach(listener => {
            try {
                listener(status);
            } catch (error) {
                console.error('[RequestManager] Listener error:', error);
            }
        });
    }

    /**
     * Reset circuit breaker (for testing or manual recovery)
     */
    reset(): void {
        this.failureCount = 0;
        this.lastFailureTime = 0;
        this.circuitState = 'closed';
        this.halfOpenRequestCount = 0;
        this.notifyListeners('healthy');
        logger.log('Circuit breaker reset');
    }
}

// Export singleton instance
export const requestManager = RequestManager.getInstance();

export default requestManager;
